/**
 * Dispensing (requisition / issue / return) row helpers.
 */

// The requisition/issue-item row PK is not consistently named across the API surface.
// `item_id` is deliberately absent — that is the product FK, not the row PK.
const LINE_ID_KEYS = [
  'line_item_id', 'issue_item_id', 'requisition_item_id', 'id', 'line_id', 'pk',
];

export function resolveLineId(row) {
  if (!row) return null;
  for (const key of LINE_ID_KEYS) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return null;
}

/**
 * Issue / requisition transaction PK.
 * Prefer `id` — confirm/detail APIs return `id` (not always `transaction_id`).
 */
export function resolveIssueId(row) {
  if (!row) return null;
  if (row.id != null && row.id !== '') return row.id;
  if (row.transaction_id != null && row.transaction_id !== '') return row.transaction_id;
  return null;
}

/** Gateway envelope markers — never treat these as issue lifecycle status. */
const GATEWAY_ENVELOPE_STATUSES = new Set([
  'Success', 'Failed', 'success', 'failed', 'SUCCESS', 'FAILED',
]);

const ISSUE_LIFECYCLE = new Set(['DRAFT', 'ISSUED', 'ACKNOWLEDGED', 'COMPLETED']);

export function isIssueLifecycleStatus(status) {
  return status != null && ISSUE_LIFECYCLE.has(status);
}

/** Lifecycle status for an issue row (list payloads vary). */
export function resolveIssueStatus(row) {
  if (!row) return null;
  const candidates = [
    row.status,
    row.issue_status,
    row.transaction_status,
    row.issue?.status,
  ];
  for (const value of candidates) {
    if (value == null || value === '') continue;
    if (GATEWAY_ENVELOPE_STATUSES.has(value)) continue;
    return value;
  }
  return null;
}

/** Normalize list/detail issue rows so UI always has id + status. */
export function normalizeIssueRow(row) {
  if (!row || typeof row !== 'object') return row;
  const id = resolveIssueId(row);
  const status = resolveIssueStatus(row);
  const next = {
    ...row,
    ...(id != null ? { id, transaction_id: row.transaction_id || id } : {}),
  };
  if (status != null) {
    next.status = status;
  } else if (GATEWAY_ENVELOPE_STATUSES.has(next.status)) {
    delete next.status;
  }
  return next;
}

export function sameIssueRow(a, b) {
  const aid = resolveIssueId(a);
  const bid = resolveIssueId(b);
  if (aid != null && bid != null) return String(aid) === String(bid);
  if (a?.transaction_number && b?.transaction_number) {
    return a.transaction_number === b.transaction_number;
  }
  return false;
}

/** After confirm/ack, merge authoritative row over a possibly stale list payload. */
export function mergeIssueIntoList(list, updated) {
  const normalized = normalizeIssueRow(updated);
  if (!normalized) return (list || []).map(normalizeIssueRow);
  const prev = (list || []).map(normalizeIssueRow);
  let found = false;
  const next = prev.map(row => {
    if (!sameIssueRow(row, normalized)) return row;
    found = true;
    // Prefer known lifecycle status from either side — never keep a gateway envelope.
    const status = resolveIssueStatus(normalized) || resolveIssueStatus(row);
    return {
      ...row,
      ...normalized,
      ...(status != null ? { status } : {}),
    };
  });
  return found ? next : [normalized, ...next];
}

/**
 * Lifecycle order — higher index = more advanced state.
 * Used to detect stale GET responses after confirm/acknowledge.
 */
const LIFECYCLE_ORDER = { DRAFT: 0, ISSUED: 1, ACKNOWLEDGED: 2, COMPLETED: 3 };

/**
 * After confirm/ack, re-fetch the issue to get fresh data.
 * However the GET can be stale (backend propagation lag) and return DRAFT even
 * after a successful confirm. In that case we use the GET's data (fields, timestamps)
 * but keep the action response's status — the action response is authoritative for status.
 */
export async function resolveIssueAfterAction(issueId, actionResponse, fetchIssue) {
  const actionNormalized = normalizeIssueRow(actionResponse);
  if (!issueId || typeof fetchIssue !== 'function') return actionNormalized;

  try {
    const fresh = await fetchIssue(issueId);
    if (!fresh) return actionNormalized;
    const freshNormalized = normalizeIssueRow(fresh);

    const actionStatus = resolveIssueStatus(actionNormalized);
    const freshStatus = resolveIssueStatus(freshNormalized);
    const actionOrder = LIFECYCLE_ORDER[actionStatus] ?? -1;
    const freshOrder = LIFECYCLE_ORDER[freshStatus] ?? -1;

    if (actionOrder > freshOrder) {
      // GET is stale — use GET's data for all fields but override status with the
      // action response's more-advanced status so UI doesn't regress to DRAFT.
      return { ...freshNormalized, status: actionStatus };
    }
    return freshNormalized;
  } catch {
    // GET failed — fall back to action response.
    return actionNormalized;
  }
}

export function toNumberOrNull(value) {
  return value === '' || value == null ? null : Number(value);
}

/** Read tenant_id/hospital_id directly off a transaction row, when it carries them. */
export function extractDispensingScope(row = {}) {
  const scope = {};
  if (row.tenant_id) scope.tenant_id = row.tenant_id;
  if (row.hospital_id) scope.hospital_id = row.hospital_id;
  return scope;
}

/**
 * Some dispensing actions (e.g. issues/from-requisition/) require tenant_id/hospital_id as
 * query params and reject the request outright when they're missing — unlike submit/approve/
 * reject, which the backend resolves from the ORG-CODE header. Requisition/issue/return rows
 * don't reliably carry these fields, so fall back to the store record the same way
 * resolveDirectGrnScope does in grnEntity.js (store rows always carry tenant/hospital scope).
 */
export function resolveDispensingScope(row = {}, stores = []) {
  const scope = extractDispensingScope(row);
  if (scope.tenant_id && scope.hospital_id) return scope;

  const storeId = row.to_store_id || row.from_store_id;
  const store = stores.find(s => String(s.store_id || s.id) === String(storeId));
  if (!scope.tenant_id && store?.tenant_id) scope.tenant_id = store.tenant_id;
  if (!scope.hospital_id && store?.hospital_id) scope.hospital_id = store.hospital_id;
  return scope;
}

/** Qty the receiver must account for on an issue line (dispatched / requested). */
export function resolveIssueLineRequestedQty(item) {
  if (!item) return 0;
  return Number(item.requested_quantity ?? item.issued_quantity ?? 0) || 0;
}

/**
 * Flatten API / axios errors into a user-facing string.
 * Handles gateway envelopes, field maps, and nested detail arrays.
 */
export function formatDispensingError(err, fallback = 'Request failed') {
  if (!err) return fallback;
  const body = err.apiResponse || err.response?.data;
  if (body && typeof body === 'object') {
    const detail = body.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
      if (Array.isArray(detail)) {
        const parts = detail.map(d => {
          if (typeof d === 'string') return d;
          if (d?.msg) return d.loc ? `${d.loc.join('.')}: ${d.msg}` : d.msg;
          return null;
        }).filter(Boolean);
        if (parts.length) return parts.join(' · ');
      }
      const fieldMsgs = Object.entries(detail)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
      if (fieldMsgs.length) return fieldMsgs.join(' · ');
    }
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (typeof body.error?.detail === 'string' && body.error.detail.trim()) return body.error.detail;
  }
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  return fallback;
}

/** Client-side acknowledge row validation. Returns error string or null. */
export function validateAcknowledgeRows(ackRows) {
  if (!ackRows?.length) {
    return 'No line items available to acknowledge. Reload and try again.';
  }
  let totalAccounted = 0;
  for (const row of ackRows) {
    if (!row.line_item_id) {
      return 'One or more lines are missing a server line ID. Reload and try again.';
    }
    const accepted = Number(row.accepted);
    const rejected = Number(row.rejected);
    if (Number.isNaN(accepted) || accepted < 0 || Number.isNaN(rejected) || rejected < 0) {
      return 'Accepted and rejected quantities must be non-negative numbers.';
    }
    // `remaining` = requested minus already-accepted/rejected (for re-acknowledge).
    // Falls back to `requested` on first-time ack where remaining === requested.
    const limit = row.remaining ?? row.requested;
    if (accepted + rejected > limit) {
      return `Accepted + rejected cannot exceed remaining unaccounted quantity (${limit}) for one or more lines.`;
    }
    if (rejected > 0 && !(row.rejection_reason || '').trim()) {
      return 'Rejection reason is required when rejected quantity is greater than zero.';
    }
    totalAccounted += accepted + rejected;
  }
  // Submitting all zeros is meaningless — at least one unit must be accounted for.
  if (totalAccounted === 0) {
    return 'At least one unit must be accepted or rejected to submit acknowledgement.';
  }
  return null;
}

/** Totals for acknowledge confirm dialog / success copy. */
export function summarizeAcknowledgeRows(ackRows) {
  return (ackRows || []).reduce((acc, row) => {
    acc.accepted += Number(row.accepted) || 0;
    acc.rejected += Number(row.rejected) || 0;
    acc.requested += Number(row.requested) || 0;
    acc.lines += 1;
    return acc;
  }, { accepted: 0, rejected: 0, requested: 0, lines: 0 });
}

/**
 * When receiving store has manager_user_id, compare to current actor.
 * Returns true / false when known; null when unknown (allow acknowledge).
 */
export function resolveIsIssueReceiver(issue, stores = [], actorId) {
  if (!issue?.to_store_id || !actorId) return null;
  const store = stores.find(s => String(s.store_id || s.id) === String(issue.to_store_id));
  if (!store?.manager_user_id) return null;
  return String(store.manager_user_id) === String(actorId);
}

/**
 * True when any line item still has unaccounted quantity (accepted + rejected < requested).
 * Used to decide whether to show the re-acknowledge form after a partial first acknowledgement.
 */
export function resolveHasUnsettledLines(items) {
  if (!items?.length) return false;
  return items.some(item => {
    const requested = Number(item.requested_quantity ?? item.issued_quantity ?? 0) || 0;
    const accepted = Number(item.accepted_quantity) || 0;
    const rejected = Number(item.rejected_quantity) || 0;
    return accepted + rejected < requested;
  });
}

export function buildIssuesListQuery({ status, to_store_id } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (to_store_id) params.set('to_store_id', to_store_id);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Requisition id from pending-issue queue row (API may nest or rename). */
export function resolvePendingRequisitionId(row) {
  if (!row) return null;
  return row.requisition_id
    || row.source_requisition_id
    || row.id
    || row.transaction_id
    || null;
}

/** Map shortage-check payload → { [item_id]: available_quantity }. */
export function mapShortageAvailableByItem(payload) {
  const list = Array.isArray(payload)
    ? payload
    : (payload?.items || payload?.lines || payload?.data || payload?.shortages || []);
  const map = {};
  if (!Array.isArray(list)) return map;
  list.forEach((row) => {
    const itemId = row?.item_id || row?.id;
    if (!itemId) return;
    const qty = row.available_quantity ?? row.central_available_quantity ?? row.available ?? row.quantity;
    if (qty != null && qty !== '') map[itemId] = Number(qty);
  });
  return map;
}

/** Friendly label for requisition lifecycle (UI only — backend enums unchanged). */
export function requisitionStatusLabel(status) {
  switch (status) {
    case 'APPROVED':
    case 'RESERVED':
      return 'Issue Pending';
    case 'PARTIALLY_ISSUED':
      return 'Partially Issued';
    case 'ISSUED':
      return 'Fully Issued';
    default:
      return status ? String(status).replace(/_/g, ' ') : '—';
  }
}
